import Image from '@atoms/Image/Image';
import { IImage } from '@models/IImage';
import BreakpointEnum from '@models/enums/BreakpointEnum';
import BreakpointTypeEnum from '@models/enums/BreakpointTypeEnum';
import SoftLoadWrapper from '@molecules/SoftLoadWrapper/SoftLoadWrapper';
import slugify from '@utils/helpers/slugify';
import useBreakpoint from '@utils/hooks/useBreakpoint';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import usePreviewMode from '@utils/hooks/usePreviewMode';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import classNames from 'classnames';
import {
  AnimatePresence,
  motion,
  useMotionValueEvent,
  useScroll,
  useSpring,
  useTransform,
} from 'framer-motion';
import { useRef, useState } from 'react';

interface Props {
  readonly title: string;
  readonly subTitle: string;
  readonly text: string;
  readonly rightImageListMobile: IImage[];
  readonly leftImageListMobile: IImage[];
  readonly rightImageListDesktop: IImage[];
  readonly leftImageListDesktop: IImage[];
  readonly backgroundImage: IImage;
  readonly centralImage: IImage;
}

const backgroundImageInitialWidths = {
  [BreakpointEnum.Small]: 336,
  [BreakpointEnum.Medium]: 648,
  [BreakpointEnum.Large]: 824,
  [BreakpointEnum.XLarge]: 1240,
};

const PreReleaseCaptureImageHighlight = ({
  title,
  subTitle,
  text,
  rightImageListMobile,
  leftImageListMobile,
  rightImageListDesktop,
  leftImageListDesktop,
  backgroundImage,
  centralImage,
}: Props) => {
  const breakPoint = useBreakpoint();
  const isMobile = useMediaQuery(1024);
  const images = isMobile
    ? [...rightImageListMobile, ...leftImageListMobile]
    : [...rightImageListDesktop, ...leftImageListDesktop];
  const scrollableContainerRef = useRef<HTMLDivElement>(null);
  const [viewportRef, { width: viewportWidth, height: viewportHeight }] =
    useResizeObserver();
  const [lastImageRef, { height: lastImageHeight }] = useResizeObserver();
  const [cameraContainerRef, { height: cameraContainerHeight }] =
    useResizeObserver();
  const [detailsSectionRef, { height: detailsSectionHeight }] =
    useResizeObserver();
  const isPreview = usePreviewMode();

  const isMobileViewport = useMediaQuery(481);
  const isMobileScreen = useMediaQuery(481, BreakpointTypeEnum.Screen);
  const isZoomedDesktop = !isMobileScreen && isMobileViewport;

  const [isTitleDisplayed, setIsTitleDisplayed] = useState(true);
  const [isCameraImageDisplayed, setIsCameraImageDisplayed] = useState(false);

  const computedPaddingBottom = detailsSectionHeight - 55;
  const imagesContainerHeight = 0.8 * viewportHeight;
  const verticalSpacingFraction =
    (imagesContainerHeight - lastImageHeight) / (images.length - 1);

  const { scrollYProgress: scrollableContainerScrollYProgress } = useScroll({
    offset: ['start end', 'end end'],
    target: scrollableContainerRef,
  });
  const smoothScrollYProgress = useSpring(scrollableContainerScrollYProgress, {
    damping: 50,
    stiffness: 250,
  });

  const initialMainContainerXPadding = breakPoint
    ? (viewportWidth - backgroundImageInitialWidths[breakPoint]) / 2
    : 0;
  const mainContainerXPadding = useTransform(
    scrollableContainerScrollYProgress,
    [0, 0.9],
    [initialMainContainerXPadding, 0]
  );

  const initialMainContainerYPadding =
    (isMobile ? 1 : 0.6) * initialMainContainerXPadding;
  const mainContainerYPadding = useTransform(
    scrollableContainerScrollYProgress,
    [0, 0.9],
    [initialMainContainerYPadding, 0]
  );

  useMotionValueEvent(scrollableContainerScrollYProgress, 'change', (value) => {
    setIsTitleDisplayed(value < 1);
    setIsCameraImageDisplayed(value === 1);
  });

  const imagesInitialBottomPosition =
    -verticalSpacingFraction * (images.length - 1);
  const imagesFinalBottomPosition = (isMobile ? 0.05 : 0.15) * viewportHeight;
  const imagesBottomPosition = useTransform(
    smoothScrollYProgress,
    [0, 0.9],
    [imagesInitialBottomPosition, imagesFinalBottomPosition]
  );

  const imageWidth = viewportWidth / images.length;
  const horizontalImagesOverflow = (4 / 3) * imageWidth;
  const imagesInitialRightPosition =
    viewportWidth + horizontalImagesOverflow / 2;
  const imagesFinalRightPosition = -horizontalImagesOverflow / 2;
  const imagesRightPosition = useTransform(
    smoothScrollYProgress,
    [0, 0.9],
    [imagesInitialRightPosition, imagesFinalRightPosition]
  );

  const gradientOpacity = useTransform(
    scrollableContainerScrollYProgress,
    [0.8, 1],
    [0, 1]
  );

  return (
    <section
      className="component-padding bg-black-100 text-white"
      data-testid="CT-153-Pre-Release-Section"
    >
      <SoftLoadWrapper>
        <div
          className="relative bg-black"
          style={{ paddingBottom: computedPaddingBottom }}
        >
          <div
            className={isPreview ? 'h-[1080px]' : 'h-[200vh]'}
            ref={scrollableContainerRef}
            data-testid="CT-153-Pre-Release-Scrollable-Container"
          >
            <div
              ref={viewportRef}
              className={classNames(
                'sticky top-0 overflow-hidden',
                isPreview ? 'h-[1080px]' : 'h-screen'
              )}
            >
              <motion.div
                style={{
                  paddingTop: mainContainerYPadding,
                  paddingBottom: mainContainerYPadding,
                  paddingLeft: mainContainerXPadding,
                  paddingRight: mainContainerXPadding,
                }}
                className="h-full"
              >
                <Image
                  image={backgroundImage}
                  imageClasses="w-full h-full object-cover"
                  classes="relative"
                  dataTestId="CT-153-Pre-Release-Background-Image"
                />

                <motion.div
                  style={{ opacity: gradientOpacity }}
                  className="absolute bottom-0 z-20 h-1/2 w-full bg-gradient-to-t from-black/100 md:h-2/5"
                  data-testid="CT-153-Pre-Release-Background-Image-Gradient"
                />

                <AnimatePresence>
                  {isTitleDisplayed && (
                    <motion.div
                      className="absolute inset-0 z-10 flex items-center justify-center max-md:px-5"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <p
                        className={classNames(
                          'text-center',
                          isZoomedDesktop ? 'mount' : 'strap'
                        )}
                        data-testid="CT-153-Pre-Release-Title"
                      >
                        {title}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>

                <motion.div
                  className="absolute flex flex-row-reverse"
                  style={{
                    width: `calc(100% + ${horizontalImagesOverflow}px)`,
                    height: imagesContainerHeight,
                    bottom: imagesBottomPosition,
                    right: imagesRightPosition,
                  }}
                  data-testid="CT-153-Pre-Release-Images-Container"
                >
                  {images.map((image, index) => (
                    <div
                      className={classNames('flex-1', {
                        'z-10':
                          index >=
                          (isMobile
                            ? rightImageListMobile.length
                            : rightImageListDesktop.length),
                      })}
                      key={slugify(`${image.desktop.url}-${index}`)}
                      style={{ marginTop: index * verticalSpacingFraction }}
                    >
                      <Image
                        image={image}
                        {...(index === images.length - 1
                          ? {
                              imageRef: lastImageRef,
                            }
                          : {})}
                        dataTestId={`CT-153-Pre-Release-Image-${index}`}
                      />
                    </div>
                  ))}
                </motion.div>
              </motion.div>
            </div>
          </div>

          <motion.div
            ref={cameraContainerRef}
            className="absolute right-0 left-0 flex items-center justify-center"
            variants={{
              hidden: {
                bottom: -(cameraContainerHeight - computedPaddingBottom),
              },
              visible: {
                bottom: 0,
              },
            }}
            initial="hidden"
            animate={isCameraImageDisplayed ? 'visible' : 'hidden'}
            transition={{
              ease: 'easeOut',
              duration: 0.7,
            }}
          >
            <div className="flex w-full flex-col items-center justify-center text-center max-md:px-5 max-lg:max-w-[550px] lg:w-1/2 xl:w-2/5">
              <div className="mb-10 lg:mb-5">
                <Image
                  image={centralImage}
                  dataTestId="CT-153-Pre-Release-Camera-Image"
                />
              </div>

              <div ref={detailsSectionRef}>
                <p
                  className="h2 mb-5 lg:mb-[35px]"
                  data-testid="CT-153-Pre-Release-Subtitle"
                >
                  {subTitle}
                </p>
                <p className="h6" data-testid="CT-153-Pre-Release-Text">
                  {text}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </SoftLoadWrapper>
    </section>
  );
};

export default PreReleaseCaptureImageHighlight;
